import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api'
import { useConnexionStore } from '@/stores/useConnexionStore'

/**
 * Panne pendant que l'app est ouverte (serveur injoignable, 429, 502-504) :
 * on garde ce qui est affiché et on le dit, comme Gmail ou Slack. Sans lui,
 * une liste illisible s'affichait « Aucune conversation » (29 sept 2026).
 *
 * Tant que la panne dure, une petite requête vérifie le retour du service,
 * de plus en plus espacée (10, 20, 40, puis 60 s) avec un décalage aléatoire.
 * Au retour, les données affichées sont rechargées.
 */
export function BandeauConnexion() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const injoignable = useConnexionStore((s) => s.injoignable)
  const etaitInjoignable = useRef(false)

  useEffect(() => {
    if (!injoignable) {
      if (etaitInjoignable.current) void queryClient.invalidateQueries()
      etaitInjoignable.current = false
      return
    }
    etaitInjoignable.current = true
    let essai = 0
    let minuteur: number
    const planifier = () => {
      const base = Math.min(10_000 * 2 ** essai, 60_000)
      minuteur = window.setTimeout(() => {
        essai += 1
        // Une réponse, quelle qu'elle soit, remet l'indicateur à jour (api.ts).
        api.get('/api/support/status/').catch(() => {}).finally(() => {
          if (useConnexionStore.getState().injoignable) planifier()
        })
      }, base * (0.8 + Math.random() * 0.4))
    }
    planifier()
    return () => window.clearTimeout(minuteur)
  }, [injoignable, queryClient])

  if (!injoignable) return null
  return (
    <div
      role="status"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-32px)] max-w-xl rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 shadow-md dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <p className="flex items-center gap-2 font-medium">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        {t('serveurInjoignable.title', 'Callizy ne répond pas pour le moment')}
      </p>
      <p className="mt-0.5 pl-6 text-xs">
        {t('serveurInjoignable.bandeau', "Certaines informations peuvent ne pas s'afficher. Rien n'a été modifié dans votre compte.")}{' '}
        {t('serveurInjoignable.support', 'Si le problème persiste, écrivez-nous à')}{' '}
        <a href="mailto:support@callizy.ca" className="font-medium underline">
          support@callizy.ca
        </a>
      </p>
    </div>
  )
}
